import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';

// Hook pour animer au scroll (Intersection Observer)
function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); obs.unobserve(el); }
    }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

export default function Home() {
  const [introPhase, setIntroPhase] = useState('logo');

  // Scroll-driven hero + transition
  const heroRef = useRef(null);
  const [sp, setSp] = useState(0);

  const handleScroll = useCallback(() => {
    if (!heroRef.current) return;
    const rect = heroRef.current.getBoundingClientRect();
    const total = heroRef.current.offsetHeight - window.innerHeight;
    const progress = Math.max(0, Math.min(1, -rect.top / total));
    setSp(progress);
  }, []);

  useEffect(() => {
    const t1 = setTimeout(() => setIntroPhase('hold'), 1500);
    const t2 = setTimeout(() => setIntroPhase('split'), 3200);
    const t3 = setTimeout(() => setIntroPhase('done'), 4200);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  // ── SCROLL PHASES ──
  const heroTextOpacity = sp < 0.1 ? 1 : Math.max(0, 1 - (sp - 0.1) / 0.15);
  const circleSize = sp < 0.25 ? 0 : Math.min(((sp - 0.25) / 0.3) * 150, 150);
  const heroImgOpacity = sp < 0.25 ? 1 : Math.max(0, 1 - (sp - 0.25) / 0.25);
  const collierScale = 1 + Math.max(0, sp - 0.25) * 0.1;

  const t1Opacity = (() => {
    if (sp < 0.50) return 0;
    if (sp < 0.58) return (sp - 0.50) / 0.08;
    if (sp < 0.72) return 1;
    if (sp < 0.78) return 1 - (sp - 0.72) / 0.06;
    return 0;
  })();

  const t2Opacity = (() => {
    if (sp < 0.75) return 0;
    if (sp < 0.83) return (sp - 0.75) / 0.08;
    if (sp < 0.95) return 1;
    return 1;
  })();

  const t1Y = (1 - t1Opacity) * 25;
  const t2Y = (1 - t2Opacity) * 25;

  return (
    <div>
      {/* ── INTRO / SPLASH SCREEN — LOGO ── */}
      {introPhase !== 'done' && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          <div className={`absolute top-0 left-0 right-0 h-1/2 bg-black ${introPhase === 'split' ? 'split-top' : ''}`} />
          <div className={`absolute bottom-0 left-0 right-0 h-1/2 bg-black ${introPhase === 'split' ? 'split-bottom' : ''}`} />
          <div className={`absolute inset-0 flex flex-col items-center justify-center ${introPhase === 'split' ? 'splash-overlay' : ''}`}
               style={introPhase === 'split' ? { animationDelay: '0.3s' } : {}}>
            {/* Logo complet */}
            <img src="/photos/Pernety_logo_fd_transparent.png" alt="Pernety"
              className="splash-logo h-40 md:h-56 lg:h-64 w-auto"
              style={{ filter: 'invert(1) brightness(2)' }} />
          </div>
        </div>
      )}

      {/* ── HERO + TRANSITION + STORYTELLING ── */}
      <section ref={heroRef} className="relative bg-black" style={{ height: '500vh' }}>
        <div className="sticky top-0 h-screen overflow-hidden">

          {/* Layer 1 : Bracelet (en dessous) */}
          <div className="absolute inset-0 pointer-events-none" style={{
            clipPath: `circle(${circleSize}% at 50% 50%)`
          }}>
            <img src="/photos/bracelet_bijou_image.png" alt="Bracelet pierres précieuses"
              className="w-full h-full object-cover"
              style={{
                filter: 'brightness(0.55) contrast(1.15) saturate(0.85)',
                transform: `scale(${collierScale})`
              }} />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 100px rgba(0,0,0,0.5)' }} />
          </div>

          {/* Layer 2 : Bague hero (au-dessus) */}
          <div className="absolute inset-0 pointer-events-none" style={{ opacity: heroImgOpacity }}>
            <img src="/photos/image_bijoux_main.png" alt="Bague opale"
              className="w-full h-full object-cover object-center md:object-[70%_center]"
              style={{ filter: 'brightness(0.7) contrast(1.1) saturate(0.85)' }} />
            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/50 to-transparent" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
            <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 150px 60px rgba(0,0,0,0.4)' }} />
          </div>

          {/* Anneau blanc subtil autour du cercle */}
          {circleSize > 2 && circleSize < 140 && (
            <div className="absolute inset-0 pointer-events-none" style={{
              background: `radial-gradient(circle at 50% 50%, transparent ${circleSize - 0.4}%, rgba(255,255,255,0.08) ${circleSize}%, transparent ${circleSize + 0.6}%)`
            }} />
          )}

          {/* Particules */}
          <div className="absolute top-1/4 right-1/3 w-1 h-1 rounded-full animate-shimmer bg-white/10" />
          <div className="absolute top-1/3 right-1/4 w-0.5 h-0.5 bg-white/15 rounded-full animate-shimmer delay-800" />

          {/* ── HERO TEXT ── */}
          <div className="absolute inset-0 flex items-center z-20"
            style={{ opacity: heroTextOpacity, transform: `translateY(${(1 - heroTextOpacity) * -30}px)` }}>
            <div className="px-8 md:px-16 lg:px-24 max-w-3xl">
              <p className="text-[10px] md:text-xs tracking-[0.5em] uppercase mb-8 font-light text-white/40">
                Joaillerie d'auteur
              </p>
              <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-[0.2em] text-white mb-4 font-light leading-none">
                PERNETY
              </h1>
              <div className="h-px mb-6 bg-white/20" style={{ width: '80px' }} />
              <p className="text-sm md:text-base font-light tracking-[0.3em] text-white/35 italic mb-12 font-serif">
                Born from Memory
              </p>
              <p className="text-white/30 text-sm md:text-base leading-relaxed max-w-md">
                Des bijoux pensés comme des fragments de mémoire.
                Chaque création naît d'un souvenir, d'une émotion, d'une lumière.
              </p>
              <Link to="/contact"
                className="inline-block mt-10 px-8 py-3.5 border border-white/20 text-white/80 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500 relative z-30">
                Prendre rendez-vous
              </Link>
            </div>
          </div>

          {/* ── TEXTE 1 — "La Maison" ── */}
          <div className="absolute inset-0 flex items-center justify-center px-6 z-10"
            style={{ opacity: t1Opacity, transform: `translateY(${t1Y}px)` }}>
            <div className="max-w-3xl text-center">
              <div className="flex items-center justify-center gap-4 mb-8">
                <div className="w-8 h-px bg-white/20" />
                <div className="w-2 h-2 rotate-45 border border-white/30" />
                <div className="w-8 h-px bg-white/20" />
              </div>

              <p className="text-xs tracking-[0.5em] uppercase mb-6 font-light text-white/50">
                La Maison
              </p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white leading-snug mb-8">
                Des bijoux pensés comme<br />des fragments de mémoire
              </h2>
              <div className="w-16 h-px mx-auto mb-8 bg-white/15" />
              <p className="text-white/40 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-6">
                Chaque création naît d'un souvenir, d'une émotion,
                d'une lumière particulière, d'un instant vécu quelque part dans le monde.
              </p>
              <p className="text-white/20 text-sm tracking-[0.3em] font-light">
                Paris — Bangkok — Le Monde
              </p>
            </div>
          </div>

          {/* ── TEXTE 2 — Citation ── */}
          <div className="absolute inset-0 flex items-center justify-center px-6 z-10"
            style={{ opacity: t2Opacity, transform: `translateY(${t2Y}px)` }}>
            <div className="max-w-3xl text-center">
              <p className="font-serif text-6xl md:text-7xl mb-4 leading-none text-white/10">"</p>

              <p className="text-white/40 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-8">
                Héritage de la terre, nos pierres sont choisies avec soin
                pour leur caractère unique, leur éclat singulier.
              </p>
              <p className="font-serif text-2xl md:text-3xl lg:text-4xl text-white/70 italic font-light leading-relaxed mb-6">
                Chaque bijou devient une présence intime,
                <br />à porter et à transmettre.
              </p>

              <div className="flex items-center justify-center gap-4 mt-10">
                <div className="w-12 h-px bg-white/20" />
                <p className="text-xs tracking-[0.4em] uppercase font-light text-white/30">Sylvie</p>
                <div className="w-12 h-px bg-white/20" />
              </div>
            </div>
          </div>

        </div>
      </section>

      <BottomSections />
    </div>
  );
}

// ── SECTIONS DU BAS ──
function BottomSections() {
  const [sylRef, sylVis] = useReveal(0.15);
  const [sylTxtRef, sylTxtVis] = useReveal(0.2);
  const [sfRef, sfVis] = useReveal(0.15);
  const [sfTxtRef, sfTxtVis] = useReveal(0.2);
  const [ctaRef, ctaVis] = useReveal(0.2);

  return (
    <>
      {/* ── SYLVIE — LA CREATRICE ── */}
      <section className="relative py-0 bg-white overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[90vh]">
          <div ref={sylRef}
            className={`md:col-span-6 relative overflow-hidden transition-all duration-[1.5s] ${sylVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <img src="/photos/Sylvie-SMP_15.jpg" alt="Sylvie — Fondatrice"
              className="w-full h-[500px] md:h-full object-cover" />
            <div className="absolute bottom-0 left-0 right-0 h-1/3 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-8 left-8">
              <p className="font-serif text-white text-2xl tracking-wider">Sylvie</p>
              <p className="text-white/50 text-xs tracking-[0.3em] uppercase mt-1">Gemmologue & Créatrice</p>
            </div>
          </div>

          <div ref={sylTxtRef}
            className={`md:col-span-6 flex items-center px-8 md:px-16 lg:px-20 py-20 md:py-0 transition-all duration-[1.5s] delay-300 ${sylTxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-gray-900" />
                <p className="text-xs tracking-[0.4em] uppercase font-medium text-gray-400">La Créatrice</p>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-gray-900 mb-10 leading-tight">
                Là où la pierre<br />rencontre l'émotion
              </h2>

              <div className="space-y-6 mb-12">
                <p className="text-gray-500 leading-[1.9] text-[15px]">
                  Gemmologue, j'ai été formée au Laboratoire Français de Gemmologie à Paris
                  puis à l'Asian Institute of Gemological Studies à Bangkok, où je me suis spécialisée dans les pierres de couleur.
                </p>
                <p className="text-gray-500 leading-[1.9] text-[15px]">
                  Curieuse et passionnée, je continue d'enrichir mes connaissances et d'affiner
                  mon regard sur les gemmes. Mes créations naissent toujours d'une pierre qui m'inspire particulièrement, autour de laquelle le bijou prend forme.
                </p>
              </div>

              <div className="flex gap-12 mb-12 py-8 border-t border-b border-gray-100">
                <div>
                  <p className="font-serif text-2xl text-gray-900">Paris</p>
                  <p className="text-xs text-gray-400 tracking-wider mt-1">Formation</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-gray-900">Bangkok</p>
                  <p className="text-xs text-gray-400 tracking-wider mt-1">Spécialisation</p>
                </div>
                <div>
                  <p className="font-serif text-2xl text-gray-900">Couleur</p>
                  <p className="text-xs text-gray-400 tracking-wider mt-1">Expertise</p>
                </div>
              </div>

              <Link to="/la-maison"
                className="inline-flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-gray-900 transition-all duration-300 group hover:opacity-60">
                Découvrir son parcours
                <span className="w-10 h-px bg-current transition-all duration-300 group-hover:w-16" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ── SAVOIR-FAIRE ── */}
      <section className="relative py-0 bg-black overflow-hidden">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-12 min-h-[90vh]">
          <div ref={sfTxtRef}
            className={`md:col-span-6 flex items-center px-8 md:px-16 lg:px-20 py-20 md:py-0 order-2 md:order-1 transition-all duration-[1.5s] delay-300 ${sfTxtVis ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div>
              <div className="flex items-center gap-3 mb-8">
                <div className="w-8 h-px bg-white/30" />
                <p className="text-xs tracking-[0.4em] uppercase font-medium text-white/40">Savoir-faire</p>
              </div>

              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-10 leading-tight">
                De la pierre<br />au bijou
              </h2>

              <div className="space-y-6 mb-12">
                <p className="text-white/40 leading-[1.9] text-[15px]">
                  Chaque création commence par une pierre qui m'inspire. Sa couleur, sa lumière,
                  son histoire guident le dessin du bijou qui prendra forme autour d'elle.
                </p>
                <p className="text-white/40 leading-[1.9] text-[15px]">
                  Un processus artisanal fait de patience, de précision et de passion,
                  où chaque détail compte pour donner vie à une pièce unique.
                </p>
              </div>

              <div className="flex items-start gap-6 mb-12">
                {[
                  { step: '01', label: 'Sélection', desc: 'La pierre' },
                  { step: '02', label: 'Dessin', desc: 'Le design' },
                  { step: '03', label: 'Création', desc: "L'artisanat" },
                ].map((item, i) => (
                  <div key={i} className="flex-1 text-center border-t border-white/10 pt-4">
                    <p className="text-xs font-light mb-1 text-white/30">{item.step}</p>
                    <p className="text-white text-sm font-medium">{item.label}</p>
                    <p className="text-white/25 text-xs mt-1">{item.desc}</p>
                  </div>
                ))}
              </div>

              <Link to="/notre-vision"
                className="inline-flex items-center gap-4 text-[11px] tracking-[0.25em] uppercase text-white/70 transition-all duration-300 group hover:text-white">
                Découvrir le processus
                <span className="w-10 h-px bg-current transition-all duration-300 group-hover:w-16" />
              </Link>
            </div>
          </div>

          <div ref={sfRef}
            className={`md:col-span-6 relative overflow-hidden order-1 md:order-2 transition-all duration-[1.5s] ${sfVis ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <img src="/photos/Sylvie-SMP_10.jpg" alt="Sylvie peignant ses créations"
              className="w-full h-[500px] md:h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent to-black/30" />
          </div>
        </div>
      </section>

      {/* ── CTA CONTACT / RDV ── */}
      <section ref={ctaRef} className="relative py-36 md:py-44 px-6 bg-white overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-b from-gray-200 to-transparent" />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-20 bg-gradient-to-t from-gray-200 to-transparent" />

        <div className={`max-w-3xl mx-auto text-center transition-all duration-[1.5s] ${ctaVis ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex items-center justify-center gap-4 mb-10">
            <div className="w-16 h-px bg-gray-200" />
            <div className="w-3 h-3 rotate-45 border border-gray-300" />
            <div className="w-16 h-px bg-gray-200" />
          </div>

          <p className="text-xs tracking-[0.5em] uppercase mb-6 font-light text-gray-400">Rendez-vous</p>

          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-gray-900 mb-6 leading-tight">
            Envie de créer<br />ensemble ?
          </h2>

          <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-4 max-w-xl mx-auto">
            Chaque bijou Pernety est une pièce unique, imaginée sur mesure.
          </p>
          <p className="text-gray-400 text-base md:text-lg leading-relaxed mb-12 max-w-xl mx-auto">
            Prenez rendez-vous pour échanger sur votre projet
            et donner vie à votre création.
          </p>

          <Link to="/contact"
            className="inline-block px-12 py-5 bg-black text-white text-[11px] tracking-[0.3em] uppercase transition-all duration-500 hover:bg-gray-800">
            Prendre rendez-vous
          </Link>

          {/* Instagram */}
          <div className="mt-16 pt-10 border-t border-gray-100">
            <a href="https://www.instagram.com/pernety_jewelry/" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-gray-400 hover:text-gray-900 transition-colors">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
              @pernety_jewelry
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
